import type { TFunction } from "i18next";
import { toast } from "sonner";

import { conversationFailure } from "@/api/agent-conversation";
import type { ActivityNode } from "@/api/config-server-stages";
import { conversationErrorText } from "@/components/agents/conversation/conversationTexts";

/*
 * Fica fora de `conversationTexts.ts` de propósito: aqueles textos são do componente
 * genérico de conversa e não citam nó; estas recusas são da abertura nos Nós 02, 03 e 04.
 * Um `t("…")` literal por código, para os gates de i18n acharem as chaves.
 */

/**
 * Recusa ao abrir a conversa de uma configuração (409/412/403 com o código no
 * `detail`), nos Nós 02, 03 e 04. Código desconhecido vira a frase genérica: o código
 * cru nunca vai à tela.
 */
export function conversationRefusalText(t: TFunction, code: string, node: ActivityNode | "org_structure"): string {
  switch (code) {
    case "conversation_already_open":
      return t("Another conversation is already open in this project.");
    case "configuration_not_set":
      return t("Configure the Configuration tenant (client 100) in Connections");
    case "cbc_connection_not_configured":
    case "cbc_connection_not_tested":
      return t("Set up and test your CBC in Connections.");
    case "configuration_login_not_configured":
      return t("Set your Configuration user and password in Connections, or use the same CBC user and password.");
    case "run_owned_by_another_user":
      return t("Only the person who started this execution can answer or stop it.");
    case "parent_not_registered":
      return t("Register the parent unit first.");
    case "no_img_activity":
      return t("No direct link for this SSCUI");
    case "item_not_approved":
      return node === "sscui"
        ? t("Approve this SSCUI before running it.")
        : t("Approve this configuration before running it.");
    case "conversation_open_in_module_config":
      return t(
        "There is already an open conversation in node 04 (App Fiori Configurations). End it before starting another.",
      );
    case "session_closing":
      return t("The browser session is closing. Try again in a moment.");
    case "row_plan_changed":
      return t("The row plan changed. Review it and try again.");
    case "conversation_open_in_org_structure":
      return t(
        "There is already an open conversation in node 02 (Organizational Structure). End it before starting another.",
      );
    case "conversation_open_in_sscui":
      return t(
        "There is already an open conversation in node 03 (SSCUI Configurations). End it before starting another.",
      );
    case "not_extracted":
      return t("No extraction: extract before running");
    default:
      return t("Could not open the conversation.");
  }
}

/** Falhas de transporte ao abrir a conversa: o aviso é o do serviço, não uma recusa. */
const TRANSPORT_FAILURES: ReadonlySet<string> = new Set(["service_unavailable", "gateway_failed", "network_failed"]);

/**
 * A frase de uma falha ao abrir a conversa, a mesma pelo ▶ e pela fila do "Run all":
 * falha de transporte vira o aviso do serviço; o resto, a recusa traduzida.
 */
export function describeOpenError(t: TFunction, error: unknown, node: ActivityNode | "org_structure"): string {
  const { code } = conversationFailure(error);
  return TRANSPORT_FAILURES.has(code) ? conversationErrorText(t, code) : conversationRefusalText(t, code, node);
}

/**
 * A aba do drawer de Connections que resolve a recusa: o CBC de quem clicou ou o login
 * dele na Configuration. As outras recusas não têm atalho.
 */
export function refusalConnectionsTarget(code: string): "cbc" | "configuration" | null {
  if (code === "cbc_connection_not_configured" || code === "cbc_connection_not_tested") return "cbc";
  if (code === "configuration_login_not_configured") return "configuration";
  return null;
}

/**
 * O aviso de falha ao abrir a conversa. A recusa de credencial ganha a ação "Open
 * Connections", que abre o drawer na aba certa (`?target=`). Sem `useNavigate`: o
 * `OrgStructureTree` também vive fora de Router.
 */
export function toastOpenError(
  t: TFunction,
  error: unknown,
  node: ActivityNode | "org_structure",
  projectId: string | undefined,
): void {
  const text = describeOpenError(t, error, node);
  const target = projectId ? refusalConnectionsTarget(conversationFailure(error).code) : null;
  if (!target) {
    toast.error(text);
    return;
  }
  toast.error(text, {
    action: {
      label: t("Open Connections"),
      onClick: () => window.location.assign(`/projects/${projectId}/connections?target=${target}`),
    },
  });
}

/**
 * O ▶ religou à conversa que já estava aberta, de outro item: o aviso cita o item dela.
 * Sem item conhecido (`null`), o aviso é genérico.
 */
export function otherConversationOpenText(t: TFunction, code: string | null): string {
  return code === null
    ? t("Another conversation is already open in this project.")
    : t("The conversation for configuration {{code}} is still open. Finish it before starting another.", { code });
}
